import axiosInstance from "./AxiosInstance"

/**
 * Case Paper Service - Single API for all case paper data
 * Fetches comprehensive patient case paper information including:
 * - Patient details
 * - Appointments history
 * - Prescriptions
 * - Reports
 * - Notes
 * - Panchakarma treatments
 * - Counsellor information
 * - Medical records
 * - Follow-up calls
 * - Questionnaires
 */
const formatDate = (date) => {
  if (!date) return "-"

  const d = new Date(date)
  const day = String(d.getDate()).padStart(2, "0")
  const month = String(d.getMonth() + 1).padStart(2, "0")
  const year = d.getFullYear()

  return `${day}-${month}-${year}`
}
/**
 * Get complete case paper data for a patient
 * @param {string} patientId - Patient ID
 * @returns {Promise<Object>} Complete case paper data
 */
export function getCasePaperData(patientId) {
  return axiosInstance.post("/patient/case-paper", {
    patient_id: patientId,
  })
}

/**
 * Format case paper data for Redux store
 * @param {Object} rawData - Raw API response
 * @returns {Object} Formatted case paper data
 */
export function formatCasePaperData(rawData) {
  if (!rawData) return null

  console.log("Raw case paper API response:", rawData)
  console.log("Latest notes in raw data:", rawData.latestNotes || rawData.latest_notes)
  console.log("Important questions in raw data:", rawData.ImportantQuestions || rawData.important_questions)

  const formattedAppointments = []

  if (rawData.timeline) {
    Object.keys(rawData.timeline).forEach((date) => {
      const dayData = rawData.timeline[date]

      const appointment = {
        id: `appointment_${date}`,
        date: date,
        time: "",
        doctor: "",
        status: "Completed",
        notes: dayData.notes || [],
        prescriptions: dayData.prescriptions || [],
        reports: dayData.reports || [],
        panchakarma: dayData.panchakarma || [],
        counsellor: dayData.counsellor || {},
        medical: dayData.medical || {},
        followup_calls: dayData.followups || [],
        male_chart: dayData.malechart || dayData.male_chart || [], // Handle both 'malechart' and 'male_chart'
        menopause: dayData.menopause || [],
        GAPLD: dayData.gapld || dayData.GAPLD || [], // Handle both 'gapld' and 'GAPLD'
        questionnaires: dayData.questionnaire || dayData.questionnaires || [],
      }

      formattedAppointments.push(appointment)
    })
  }

  const formattedData = {
    // Patient Information
    patient: {
      id: rawData.patient?.patient_id || "",
      name: rawData.patient?.name || "",
      contact: rawData.patient?.contact || "",
      email: rawData.patient?.email || "",
      profileImage: rawData.patient?.profile_image || "",
      gender: rawData.patient?.gender || "",
      age: rawData.patient?.age || "",
      dob: rawData.patient?.dob || "",
      height: rawData.patient?.height || "",
      weight: rawData.patient?.weight || "",
      occupation: rawData.patient?.occupation || "",
      branch: rawData.patient?.branch || "",
      center: rawData.patient?.center || "",
      additionalContact: rawData.patient?.additional_contact || "",
      marriageHistory: rawData.patient?.marriage_history || "",
      marriageDate: rawData.patient?.marriage_date || "",
      spouseName: rawData.patient?.spouse_name || "",
      spouseOption: rawData.patient?.spouse_option || "",
      diseases: rawData.patient?.diseases || [],
      diseaseStatus: rawData.patient?.disease_status || "",
      currentDisease: rawData.patient?.current_disease || "",
          // ✅ ADD THESE NEW FIELDS
    firstAppointment: rawData.patient?.first_appointment || "",
    firstAppointmentDuration: rawData.patient?.duration_first_appointment || "",
    medicineTaken: rawData.patient?.medicine_taken || "",
    numberOfVisits: rawData.patient?.no_of_visit || "0",
    numberOfMedicinesTaken: rawData.patient?.no_of_medicine_taken || "0",

    latestFollowupDate: formatDate(rawData.patient?.latest_followup_date),
    nextAppointmentDate: formatDate(rawData.patient?.next_appointment_date),
      // Address Information
      addresses: {
        temporary: rawData.patient?.temporary_address || {},
        permanent: rawData.patient?.permanent_address || {},
      },
      // Other Information
      otherInfo: rawData.patient?.other_info || {},
    },
    offlineVisitCount:
    rawData.OfflineVisitCount ||
    rawData.patient?.OfflineVisitCount ||
    rawData.offlineVisitCount ||
    0,
  
  onlineVisitCount:
    rawData.OnlineVisitCount ||
    rawData.patient?.OnlineVisitCount ||
    rawData.onlineVisitCount ||
    0,
    appointments: formattedAppointments,

    // Prescriptions
    prescriptions:
      rawData.prescriptions?.map((prescription) => ({
        id: prescription.prescription_id || "",
        date: prescription.prescription_date || "",
        doctor: prescription.doctor_name || "",
        medicines:
          prescription.medicines?.map((medicine) => ({
            id: medicine.medicine_id || "",
            name: medicine.medicine_name || "",
            dose: medicine.dose || "",
            frequency: medicine.frequency || "",
            quantity: medicine.quantity || "",
            deliveredQuantity: medicine.updated_qty || "",
            howToTake: medicine.how_to_take || "",
            status: medicine.status || "",
            duration: medicine.duration || "-",
          })) || [],
        status: prescription.status || "",
      })) || [],

    // Reports
    reports:
      rawData.reports?.map((report) => ({
        id: report.report_id || "",
        date: report.report_date || "",
        type: report.report_type || "",
        title: report.report_title || "",
        description: report.description || "",
        fileUrl: report.file_url || "",
        thumbnailUrl: report.thumbnail_url || "",
        uploadedBy: report.uploaded_by || "",
        status: report.status || "",
      })) || [],

    // Notes
    notes:
      rawData.notes?.map((note) => ({
        id: note.note_id || "",
        title: note.note_title || "",
        description: note.note_description || "",
        type: note.note_type || "",
        createdBy: note.created_by_name || "",
        createdAt: note.created_at || "",
        updatedAt: note.updated_at || "",
        status: note.status || "",
      })) || [],

    // Latest Notes (most recent notes)
    latestNotes: rawData.latestNotes || rawData.latest_notes || rawData.notes || [],

    // Important Questions
    ImportantQuestions: rawData.ImportantQuestions || rawData.important_questions || rawData.importantQuestions || [],

    // Panchakarma Treatments
    panchakarma:
      rawData.panchakarma?.map((treatment) => ({
        id: treatment.panchakarma_id || "",
        name: treatment.panchakarma_name || "",
        startDate: treatment.start_date || "",
        endDate: treatment.end_date || "",
        duration: treatment.duration || "",
        status: treatment.status || "",
        doctor: treatment.doctor_name || "",
        notes: treatment.notes || "",
        recommendations: treatment.recommendations || [],
      })) || [],

    // Counsellor Information
    counsellor:
      rawData.counsellor?.map((session) => ({
        id: session.counsellor_id || "",
        date: session.session_date || "",
        counsellorName: session.counsellor_name || "",
        medicineExplainedDate: session.medicine_explained_date || "",
        medicineExplainedBy: session.medicine_explained_by || "",
        notes: session.notes || "",
        recommendations: session.recommendations || [],
      })) || [],

    // Medical Records
    medical:
      rawData.medical?.map((record) => ({
        id: record.medical_id || "",
        date: record.record_date || "",
        type: record.record_type || "",
        preparedBy: record.prepared_by || "",
        sentDate: record.sent_date || "",
        deliveryDate: record.delivery_date || "",
        deliveryMethod: record.delivery_method || "",
        status: record.status || "",
        notes: record.notes || "",
      })) || [],

    // Follow-up Calls
    followupCalls:
      rawData.followup_calls?.map((call) => ({
        id: call.followup_id || "",
        date: call.followup_date || "",
        caller: call.caller_name || "",
        status: call.status || "",
        notes: call.notes || "",
        nextFollowupDate: call.next_followup_date || "",
        actionRequired: call.action_required || "",
      })) || [],

    // Questionnaires
    questionnaires:
      rawData.questionnaires?.map((questionnaire) => ({
        id: questionnaire.questionnaire_id || "",
        date: questionnaire.questionnaire_date || "",
        type: questionnaire.questionnaire_type || "",
        questions:
          questionnaire.questions?.map((q) => ({
            id: q.question_id || "",
            question: q.question_text || "",
            answer: q.answer || "",
            category: q.category || "",
          })) || [],
        status: questionnaire.status || "",
      })) || [],

    maleChart: formattedAppointments.reduce((acc, appointment) => {
      if (appointment.male_chart && appointment.male_chart.length > 0) {
        appointment.male_chart.forEach((chart) => {
          acc.push({
            id: chart.male_chart_id || "",
            patientId: chart.patient_id || "",
            date: chart.malechart_date || chart.date || "",
            symptoms: chart.symptoms_intercourse || chart.symptoms || "",
            metallicColor: chart.metalic_color || chart.metallic_color || "",
            status: chart.male_chart_status || chart.status || "",
            createdBy: chart.created_by || "",
            createdAt: chart.created_at || "",
            updatedAt: chart.updated_at || "",
          })
        })
      }
      return acc
    }, []),

    menopause: formattedAppointments.reduce((acc, appointment) => {
      if (appointment.menopause && appointment.menopause.length > 0) {
        appointment.menopause.forEach((meno) => {
          acc.push({
            id: meno.menopause_id || "",
            patientId: meno.patient_id || "",
            menopauseDate: meno.menopause_date || "",
            ageFirstMenstruation: meno.age_first_m || "",
            menstruation: meno.menstruation || "",
            cycleDays: meno.how_many_days_occur || "",
            stoppedSince: meno.how_long_m_stopped || "",
            causes: meno.causes_m || "",
            otherNotes: meno.other_name || "",
            status: meno.menopause_status || "",
            createdAt: meno.created_at || "",
            createdBy: meno.created_by || "",
            updatedAt: meno.updated_at || "",
          })
        })
      }
      return acc
    }, []),

    GAPLD: formattedAppointments.reduce((acc, appointment) => {
      if (appointment.GAPLD && appointment.GAPLD.length > 0) {
        appointment.GAPLD.forEach((gapld) => {
          acc.push({
            id: gapld.gapld_id || "",
            patientId: gapld.patient_id || "",
            pregnancyStatus: gapld.pregnancy_status || "",
            deliveryDate: gapld.delivery_date || "",
            howWasDelivery: gapld.How_was_the_delivery || "",
            gender: gapld.gender || "",
            abortionDate: gapld.abortion_date || "",
            whichRxHelped: gapld.which_rx_helped || "",
            howDidMiscarriageHappen: gapld.how_did_miscarriage_happen || "",
            miscarriageReason: gapld.miscarriage_reason || "",
            gapldStatus: gapld.gapld_status || "",
            marriageInfoId: gapld.marriage_info_id || "",
            marriageHistory: gapld.marriage_history || "",
            createdBy: gapld.created_by || "",
            createdAt: gapld.created_at || "",
            updatedAt: gapld.updated_at || "",
          })
        })
      }
      return acc
    }, []),

    // Statistics and Counts
    statistics: {
      totalAppointments: rawData.statistics?.total_appointments || 0,
      totalPrescriptions: rawData.statistics?.total_prescriptions || 0,
      totalReports: rawData.statistics?.total_reports || 0,
      totalNotes: rawData.statistics?.total_notes || 0,
      totalPanchakarma: rawData.statistics?.total_panchakarma || 0,
      totalFollowupCalls: rawData.statistics?.total_followup_calls || 0,
      doctorNoteCount: rawData.statistics?.doctor_note_count || 0,
      telecallerNoteCount: rawData.statistics?.telecaller_note_count || 0,
      followupCallCount: rawData.statistics?.followup_call_count || 0,
    },

    // Platform and Campaign Information
    platform: {
      source: rawData.platform?.source || "",
      campaign: rawData.platform?.campaign || "",
      adSet: rawData.platform?.ad_set || "",
      leadSource: rawData.platform?.lead_source || "",
      status: rawData.platform?.status || "",
      priority: rawData.platform?.priority || "",
    },

    // Last Updated
    lastUpdated: rawData.last_updated || new Date().toISOString(),
  }

  console.log("Formatted case paper data:", formattedData)
  console.log("Formatted latest notes:", formattedData.latestNotes)
  console.log("Formatted important questions:", formattedData.ImportantQuestions)

  return formattedData
}

/**
 * Get case paper data with formatting
 * @param {string} patientId - Patient ID
 * @returns {Promise<Object>} Formatted case paper data
 */
export function getFormattedCasePaperData(patientId) {
  return getCasePaperData(patientId)
    .then((response) => {
      if (response?.status === 200) {
        return response.data
      } else {
        throw new Error(response?.data?.message || "Failed to fetch case paper data")
      }
    })
    .catch((error) => {
      console.error("Error getting formatted case paper data:", error)
      throw error
    })
}
